import apiCall from "@/func/net/apiCall";
import userHasRole from "@/func/user/userHasRole";
import { SfUser, SfUserRole, SfApiError, SfAccountType } from "@/types/generalTypes";
import { computed, ref } from "vue";
import { RouteLocationNormalizedLoaded } from "vue-router";

let lastLoadingQueveUid = 0;
let lastApiErrorUid = 0;
type UserLoadStatus = "inited" | "loading" | "loaded" | "failed";

const MATHS_AVLB = {
  play: {
    school: true,
    individual: true,
    mf: true,
    guest: true,
  },
  db: {
    school: true,
    individual: true,
    mf: true,
    guest: true,
  },
  store: {
    school: true,
    individual: true,
    mf: true,
    guest: true,
  },
}

export const isMathsAvailable = (
  accountKind: SfAccountType | 'guest', 
  accessKind: "play" | "db" | "store"
) => {
  return MATHS_AVLB[accessKind][accountKind];
}


interface LoadingQueveItem {
  uid: number;
  message: string;
}

const appState = ref({
  userLoadStatus: "inited" as UserLoadStatus,
  user: null as null | SfUser,
  loadingQueve: [] as LoadingQueveItem[],
  contactVisible: false,
  loginVisible: false,
  faqVisible: false,
  isV1Legacy: false,
  apiErrors: [] as SfApiError[],
  prevenScrollCount: 0,
});

// TODO remove legacy when possible
export const isV1Legacy = computed(() => appState.value.isV1Legacy);
export const setIsV1Legacy = (isLegacy: boolean) => {
  appState.value.isV1Legacy = isLegacy;
};

export const contactVisible = computed(() => appState.value.contactVisible);
export const setContactVisible = (visible: boolean) => {
  appState.value.contactVisible = visible;
};

export const loginVisible = computed(() => appState.value.loginVisible);
export const setLoginVisible = (visible: boolean) => {
  appState.value.loginVisible = visible;
};

export const faqVisible = computed(() => appState.value.faqVisible);
export const setFaqVisible = (visible: boolean) => {
  appState.value.faqVisible = visible;
};

export const siteUser = computed(() => appState.value.user);
export const setUserState = (
  user: null | SfUser,
  loadStatus: UserLoadStatus,
) => {
  if (user === undefined) {
    return;
  }
  appState.value.user = user;
  appState.value.userLoadStatus = loadStatus;
};

export const setIndividualMathsLevel = (level: number) => {
  if (!appState.value.user) {
    return;
  }
  appState.value.user.maths_level = level;
};

export const userLoadStatus = computed(() => appState.value.userLoadStatus);
export const userSchool = computed(() => appState.value.user?.school);
export const loadingQueve = computed(() => appState.value.loadingQueve);

export const refreshUser = async () => {
  const resData = await apiCall("auth/info");
  if (resData.success) {
    setUserState(resData.data.user, "loaded");
    if ((window as any).sfSetReactUser) {
      (window as any).sfSetReactUser(resData.data.user);
    }
  }
};

export const getCurrentPage = (route: RouteLocationNormalizedLoaded) => {
  if (route.query.page) {
    return parseInt(route.query.page as string);
  }
  return 1;
};

export const addToLoadingQueve = (message = "Loading") => {
  lastLoadingQueveUid++;
  appState.value.loadingQueve.push({ uid: lastLoadingQueveUid, message });
  return lastLoadingQueveUid;
};

export const removeFromLoadingQueve = (uid: number) => {
  const pos = appState.value.loadingQueve.findIndex((el) => el.uid == uid);
  if (pos >= 0) {
    appState.value.loadingQueve.splice(pos, 1);
  }
};

export const apiErrors = computed(() => appState.value.apiErrors);

export const addToApiErrors = (apiError: Omit<SfApiError, "uid">) => {
  lastApiErrorUid++;
  appState.value.apiErrors.push({ ...apiError, uid: lastApiErrorUid });
};

export const removeFomApiErrors = (uid: number) => {
  const pos = appState.value.apiErrors.findIndex((el) => el.uid == uid);
  if (pos >= 0) {
    appState.value.apiErrors.splice(pos, 1);
  }
};

export const clearApiErrors = () => {
  appState.value.apiErrors = [];
};

export function userHasLegacyRole(user: SfUser | null) {
  if (!user) {
    return false;
  }
  const legacyRoles = [
    "sfAdmin",
    "sfEditor",
    "individualAdmin",
  ] as SfUserRole[];
  return userHasRole(user, legacyRoles);
}

export const preventScroll = (prevent: boolean) => {
  let newVal = appState.value.prevenScrollCount + (prevent ? 1 : -1);
  if (newVal < 0) {
    newVal = 0;
  }
  appState.value.prevenScrollCount = newVal;
  if (appState.value.prevenScrollCount == 1) {
    document.body.classList.add("prevent-scroll");
  } else if (appState.value.prevenScrollCount == 0) {
    document.body.classList.remove("prevent-scroll");
  }
};
